import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'

interface User {
  id: string
  email: string
  user_metadata?: {
    avatar_url?: string
    full_name?: string
  }
  tos_accepted?: boolean
}

interface Server {
  id: string
  name: string
  type: string
  version: string
  status: 'online' | 'offline' | 'maintenance'
  players: number
  maxPlayers: number
  active?: boolean
  seconds_active?: number
  serverStartTime?: string
  ip?: string
  plan_id?: string
  plan?: {
    id: string
    label: string
    description: string
    price: number
    location?: string
  }
  game?: string
  permissions?: string
  /** Globally-unique subdomain (e.g. for *.node.voltary.org). null = unassigned. */
  subdomain?: string | null
  /** Whether the server has been provisioned with a VPS at least once.
   *  Replaces the old `!!server.ip` gate (IP is no longer exposed). */
  provisioned?: boolean
  /** ISO 8601 timestamp from the DB; used by Dashboard to keep
   *  newest-first ordering after SSE patches. */
  created_at?: string
}

interface Payment {
  id: string
  date: string
  amount: number
  description: string
  orderId?: string
}

interface Transfer {
  id: number
  fromUserId: string
  toUserId: string
  amount: number
  concept: string
  date: string
  type: 'sent' | 'received'
}

interface GameVersions {
  [game: string]: {
    [type: string]: { id: string; name: string; popular: boolean; deploy?: string }[]
  }
}

interface ServerConfig {
  maxPlayers: number
  maxServersPerUser?: number
}

interface Referral {
  referrer: string;
  referee: string;
  created_at: string;
  referee_name?: string;
}

interface Notification {
  created_at: string;
  version: string;
  description: string;
}

/**
 * Snapshot of the maintenance window that currently blocks the platform,
 * or `null` if no window is active. The endpoint is public, so this is
 * populated regardless of auth state.
 */
interface MaintenanceInfo {
  active: boolean;
  reason: string;
  end: string;
}

interface AppState {
  user: User | null
  balance: number
  servers: Server[]
  payments: Payment[]
  transfers: Transfer[]
  versions: GameVersions | null
  config: ServerConfig | null
  referralCode: string | null
  referrals: Referral[]
  useRefediroCode: boolean | null
  notifications: Notification[]
  maintenance: MaintenanceInfo | null
}

interface AppContextType {
  state: AppState
  isLoading: boolean
  // User actions
  fetchUser: () => Promise<void>
  updateUser: (user: User) => void
  // Balance actions
  fetchBalance: () => Promise<void>
  updateBalance: (balance: number) => void
  // Servers actions
  fetchServers: (opts?: { force?: boolean }) => Promise<Server[]>
  updateServers: (servers: Server[]) => void
  addServer: (server: Server) => void
  removeServer: (serverId: string) => void
  updateServer: (serverId: string, updates: Partial<Server>) => void
  // Payments actions
  fetchPayments: () => Promise<Payment[]>
  updatePayments: (payments: Payment[]) => void
  // Transfers actions
  fetchTransfers: () => Promise<Transfer[]>
  updateTransfers: (transfers: Transfer[]) => void
  // Versions and config actions
  fetchVersions: () => Promise<void>
  updateVersions: (versions: GameVersions) => void
  fetchConfig: () => Promise<void>
  updateConfig: (config: ServerConfig) => void
  // Referral actions
  fetchReferralCode: () => Promise<void>
  fetchReferrals: () => Promise<Referral[]>
  fetchReferralStatus: () => Promise<boolean | null>
  setReferralStatus: (wasInvited: boolean, referralCode?: string) => Promise<{ success: boolean; error?: string; referrerName?: string }>
  // Notifications actions
  fetchNotifications: () => Promise<void>
  // Maintenance: refresh the public /api/maintenance/active snapshot
  fetchMaintenance: () => Promise<void>
  // Combined fetch
  fetchDashboardData: () => Promise<void>
  // Clear cache
  clearCache: () => void
  // Drop a single cache key so the next fetch of that key hits the API
  // instead of returning a stale value. Used by the SSE event handler.
  invalidateCache: (key: string) => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

// Cache duration in milliseconds. Server state changes frequently
// (create/delete/start/stop) so a 5-minute TTL made stale data visible
// across pages. 10 seconds is short enough to feel fresh but still
// covers rapid navigation between Dashboard and ServerPanel.
const CACHE_DURATION = 10 * 1000

interface CacheEntry<T> {
  data: T
  timestamp: number
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    user: null,
    balance: 0,
    servers: [],
    payments: [],
    transfers: [],
    versions: null,
    config: null,
    referralCode: null,
    referrals: [],
    useRefediroCode: null,
    notifications: [],
    maintenance: null,
  })
  const [isLoading, setIsLoading] = useState(false)

  // Cache for API responses
  const [cache, setCache] = useState<Record<string, CacheEntry<unknown>>>({})

  const getCached = <T,>(key: string): T | null => {
    const entry = cache[key]
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry.data as T
    }
    return null
  }

  const setCached = <T,>(key: string, data: T) => {
    setCache(prev => ({
      ...prev,
      [key]: { data, timestamp: Date.now() }
    }))
  }

  // Remove a single cache key. Used by the SSE event handler so that
  // when the source-of-truth changes in another tab, the next fetch
  // goes to the API instead of returning a stale cached value.
  const invalidateCache = useCallback((key: string) => {
    setCache(prev => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const fetchUser = useCallback(async () => {
    // Check cache first
    const cached = getCached<User>('user')
    if (cached) {
      setState(prev => ({ ...prev, user: cached }))
      return
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/profile`, { credentials: 'include' })
      const data = await res.json()
      
      if (res.ok) {
        // The profile endpoint now returns user and tos_accepted
        const userWithTos = {
          ...data.user,
          tos_accepted: data.tos_accepted
        }
        setState(prev => ({ ...prev, user: userWithTos }))
        setCached('user', userWithTos)
      }
    } catch (err) {
      console.error('Error fetching user:', err)
    }
  }, [])

  const updateUser = useCallback((user: User) => {
    setState(prev => ({ ...prev, user }))
    setCached('user', user)
  }, [])

  const fetchBalance = useCallback(async () => {
    const cached = getCached<number>('balance')
    if (cached !== null) {
      setState(prev => ({ ...prev, balance: cached }))
      return
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/profile/cash`, { credentials: 'include' })
      const data = await res.json()

      if (res.ok) {
        const balance = data.cash || 0
        setState(prev => ({ ...prev, balance }))
        setCached('balance', balance)
      }
    } catch (err) {
      console.error('Error fetching balance:', err)
    }
  }, [])

  const updateBalance = useCallback((balance: number) => {
    setState(prev => ({ ...prev, balance }))
    setCached('balance', balance)
  }, [])

  const fetchServers = useCallback(async (opts?: { force?: boolean }): Promise<Server[]> => {
    // `force: true` bypasses the cache — used by the SSE event handler
    // when we know the source-of-truth changed (server created/deleted/updated
    // in another tab) and the cached payload is now stale.
    if (!opts?.force) {
      const cached = getCached<Server[]>('servers')
      if (cached) {
        setState(prev => ({ ...prev, servers: cached }))
        return cached
      }
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers`, { credentials: 'include' })
      const data = await res.json()

      if (res.ok) {
        const servers = data.servers || []
        setState(prev => ({ ...prev, servers }))
        setCached('servers', servers)
        return servers
      }
    } catch (err) {
      console.error('Error fetching servers:', err)
    }
    return []
  }, [])

  const updateServers = useCallback((servers: Server[]) => {
    setState(prev => ({ ...prev, servers }))
    setCached('servers', servers)
  }, [])

  const addServer = useCallback((server: Server) => {
    setState(prev => {
      const newServers = [...prev.servers, server]
      setCached('servers', newServers)
      return { ...prev, servers: newServers }
    })
  }, [])

  const removeServer = useCallback((serverId: string) => {
    setState(prev => {
      const newServers = prev.servers.filter(s => String(s.id) !== String(serverId))
      setCached('servers', newServers)
      return { ...prev, servers: newServers }
    })
  }, [])

  const updateServer = useCallback((serverId: string, updates: Partial<Server>) => {
    setState(prev => {
      const newServers = prev.servers.map(s => 
        String(s.id) === String(serverId) ? { ...s, ...updates } : s
      )
      setCached('servers', newServers)
      return { ...prev, servers: newServers }
    })
  }, [])

  const fetchPayments = useCallback(async (): Promise<Payment[]> => {
    const cached = getCached<Payment[]>('payments')
    if (cached && cached.length > 0) {
      setState(prev => ({ ...prev, payments: cached }))
      return cached
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/payments`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        const payments = data.payments || []
        setState(prev => ({ ...prev, payments }))
        setCached('payments', payments)
        return payments
      }
    } catch (err) {
      console.error('Error fetching payments:', err)
    }
    return []
  }, [])

  const fetchTransfers = useCallback(async (): Promise<Transfer[]> => {
    const cached = getCached<Transfer[]>('transfers')
    if (cached && cached.length > 0) {
      setState(prev => ({ ...prev, transfers: cached }))
      return cached
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/transfers`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        const transfers = data.transfers || []
        setState(prev => ({ ...prev, transfers }))
        setCached('transfers', transfers)
        return transfers
      }
    } catch (err) {
      console.error('Error fetching transfers:', err)
    }
    return []
  }, [])

  const updateTransfers = useCallback((transfers: Transfer[]) => {
    setState(prev => ({ ...prev, transfers }))
    setCached('transfers', transfers)
  }, [])

  const updatePayments = useCallback((payments: Payment[]) => {
    setState(prev => ({ ...prev, payments }))
    setCached('payments', payments)
  }, [])

  const fetchVersions = useCallback(async () => {
    const cached = getCached<GameVersions>('versions')
    if (cached) {
      setState(prev => ({ ...prev, versions: cached }))
      return
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/versions`)
      const data = await res.json()
      
      if (data.versions) {
        setState(prev => ({ ...prev, versions: data.versions }))
        setCached('versions', data.versions)
      }
    } catch (err) {
      console.error('Error fetching versions:', err)
    }
  }, [])

  const updateVersions = useCallback((versions: GameVersions) => {
    setState(prev => ({ ...prev, versions }))
    setCached('versions', versions)
  }, [])

  const fetchConfig = useCallback(async () => {
    const cached = getCached<ServerConfig>('config')
    if (cached) {
      setState(prev => ({ ...prev, config: cached }))
      return
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/config`)
      const data = await res.json()
      
      if (data) {
        setState(prev => ({ ...prev, config: data }))
        setCached('config', data)
      }
    } catch (err) {
      console.error('Error fetching config:', err)
    }
  }, [])

  const updateConfig = useCallback((config: ServerConfig) => {
    setState(prev => ({ ...prev, config }))
    setCached('config', config)
  }, [])

  const fetchReferralCode = useCallback(async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/referrals/code`, { credentials: 'include' })
      const data = await res.json()
      
      if (res.ok && data.referralCode) {
        setState(prev => ({ ...prev, referralCode: data.referralCode }))
      }
    } catch (err) {
      console.error('Error fetching referral code:', err)
    }
  }, [])

  const fetchReferrals = useCallback(async (): Promise<Referral[]> => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/referrals`, { credentials: 'include' })
      const data = await res.json()
      
      if (res.ok && data.referrals) {
        setState(prev => ({ ...prev, referrals: data.referrals }))
        return data.referrals
      }
    } catch (err) {
      console.error('Error fetching referrals:', err)
    }
    return []
  }, [])

  const fetchReferralStatus = useCallback(async (): Promise<boolean | null> => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/referrals/status`, { credentials: 'include' })
      const data = await res.json()
      
      if (res.ok && data.useRefediroCode !== undefined) {
        setState(prev => ({ ...prev, useRefediroCode: data.useRefediroCode }))
        return data.useRefediroCode
      }
      return null
    } catch (err) {
      console.error('Error fetching referral status:', err)
      return null
    }
  }, [])

  const setReferralStatus = useCallback(async (wasInvited: boolean, referralCode?: string): Promise<{ success: boolean; error?: string; referrerName?: string }> => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/referrals/set-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wasInvited, referralCode })
      })
      const data = await res.json()
      
      if (res.ok && data.success) {
        setState(prev => ({ ...prev, useRefediroCode: wasInvited }))
        return { success: true, referrerName: data.referrerName }
      }
      return { success: false, error: data.error }
    } catch (err) {
      console.error('Error setting referral status:', err)
      return { success: false, error: 'Error de conexión' }
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/notifications`, { credentials: 'include' })
      const data = await res.json()

      if (res.ok && data.notifications) {
        setState(prev => ({ ...prev, notifications: data.notifications }))
      }
    } catch (err) {
      console.error('Error fetching notifications:', err)
    }
  }, [])

  /**
   * Polls the public maintenance endpoint. Endpoint is unauthenticated, so
   * we don't send cookies. Returns silently on failure: if the API is
   * down, the user simply doesn't see the banner — preferable to blocking
   * the whole UI on a transient read error.
   */
  const fetchMaintenance = useCallback(async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/maintenance/active`)
      if (!res.ok) return
      const data = await res.json()
      const info: MaintenanceInfo | null = data.active && data.maintenance
        ? {
            active: true,
            reason: data.maintenance.reason,
            end: data.maintenance.end,
          }
        : null
      setState(prev => ({ ...prev, maintenance: info }))
    } catch (err) {
      console.error('Error fetching maintenance status:', err)
    }
  }, [])

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true)
    try {
      await Promise.all([
        fetchUser(),
        fetchBalance(),
        fetchServers(),
        fetchPayments(),
        fetchTransfers(),
        fetchVersions(),
        fetchConfig(),
        fetchNotifications(),
        fetchMaintenance(),
      ])
    } catch (err) {
      console.error('Error fetching dashboard data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [fetchUser, fetchBalance, fetchServers, fetchPayments, fetchTransfers, fetchVersions, fetchConfig, fetchNotifications, fetchMaintenance])

  const clearCache = useCallback(() => {
    setCache({})
    setState({
      user: null,
      balance: 0,
      servers: [],
      payments: [],
      transfers: [],
      versions: null,
      config: null,
      referralCode: null,
      referrals: [],
      useRefediroCode: null,
      notifications: [],
      maintenance: null,
    })
  }, [])

  const value: AppContextType = {
    state,
    isLoading,
    fetchUser,
    updateUser,
    fetchBalance,
    updateBalance,
    fetchServers,
    updateServers,
    addServer,
    removeServer,
    updateServer,
    fetchPayments,
    updatePayments,
    fetchTransfers,
    updateTransfers,
    fetchVersions,
    updateVersions,
    fetchConfig,
    updateConfig,
    fetchReferralCode,
    fetchReferrals,
    fetchReferralStatus,
    setReferralStatus,
    fetchNotifications,
    fetchMaintenance,
    fetchDashboardData,
    clearCache,
    invalidateCache,
  }

  // Poll maintenance status independently of dashboard data: the banner
  // has to react immediately when an admin opens/closes a maintenance
  // window, even if the user is sitting on a non-dashboard page. 30s is
  // short enough to feel live but light enough to not hammer the API.
  useEffect(() => {
    void fetchMaintenance()
    const interval = setInterval(() => {
      void fetchMaintenance()
    }, 30 * 1000)
    return () => clearInterval(interval)
  }, [fetchMaintenance])

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
